import { useMemo } from "react";
import Select from "../../../shared/ui/controls/Select/Select";
import { useDebounce } from "../../../shared/hooks/useDebounce";
import { useAppointmentsByWorkOrder } from "../api/queries/useAppointmentsByWorkOrder";

/**
 * Pick one Termin of one Auftrag.
 *
 * Two steps rather than one search box because the API has no appointment list — only
 * `GET /appointments/by-work-order/:workOrderId` and `GET /appointments/:id`. That happens to
 * match how the Termin is named out loud anyway ("der zweite Termin zu 12345"), and the answer
 * is short and unpaginated, so a plain `Select` is the whole control.
 */

const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString("de-DE", {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });

interface Props {
  /** The Auftrag whose Termine are offered. Empty means the field has nothing to show yet. */
  workOrderId: string;
  value: number | null;
  onChange: (id: number | null) => void;
  disabled?: boolean;
  error?: string | null;
  className?: string;
}

export function AppointmentByWorkOrderSelect({
  workOrderId,
  value,
  onChange,
  disabled,
  error,
  className,
}: Props) {
  const trimmedWorkOrderId = workOrderId.trim();
  /**
   * The Auftragsnummer is typed, not picked, so every keystroke is a prefix of it — and each
   * prefix is a perfectly valid id as far as the endpoint is concerned. Querying them all walks
   * straight into the API's rate limiter: an eight-digit number produced eight requests, the
   * last of which came back 429, so the real number was the one answer never fetched.
   */
  const settledWorkOrderId = useDebounce(trimmedWorkOrderId);
  const isSettling = settledWorkOrderId !== trimmedWorkOrderId;

  const { data, isLoading, isError } = useAppointmentsByWorkOrder(settledWorkOrderId || null);

  const options = useMemo(
    () =>
      (data ?? []).map((appointment) => ({
        value: appointment.id,
        label: [
          `Termin ${appointment.sequenceNumber}`,
          formatWhen(appointment.schedule.plannedWorkStartsAt),
          appointment.technician?.name,
        ]
          .filter(Boolean)
          .join(" · "),
      })),
    [data],
  );

  /**
   * The placeholder is the only thing telling the user why the list is empty, so it names the
   * actual reason rather than repeating "Termin wählen" in all four situations.
   */
  const placeholder = !trimmedWorkOrderId
    ? "Zuerst Auftragsnummer eingeben"
    : // Still settling counts as loading: saying "keine Termine" about a half-typed number
      // would flicker a wrong answer on every keystroke.
      isSettling || isLoading
      ? "Termine werden geladen…"
      : isError
        ? "Termine konnten nicht geladen werden"
        : options.length === 0
          ? "Keine Termine zu diesem Auftrag"
          : "Termin wählen";

  return (
    <Select
      options={options}
      value={value}
      onChange={(next) => onChange(next === null ? null : Number(next))}
      placeholder={placeholder}
      /*
       * Locked while settling too. The query key still carries the previous Auftrag until the
       * debounce fires, so `options` are that Auftrag's Termine — pickable, and silently wrong
       * once the number above them has already changed.
       */
      disabled={disabled || isSettling || options.length === 0}
      error={error}
      className={className}
    />
  );
}
